import React, { useState } from "react";
import { Client, Databases, ID, Query } from "appwrite";
import emailjs from '@emailjs/browser';

// Initialize Appwrite client
const client = new Client();
client
  .setEndpoint("https://cloud.appwrite.io/v1")
  .setProject("67fbee6a001260770104");

const databases = new Databases(client);

const DATABASE_ID = "68098bc80034d68e4e64";
const NEWSLETTER_COLLECTION_ID = "68fcb30e000835755071";
const CONTACT_COLLECTION_ID = "68fe1b46002f1c1e81a6"; // Create this collection

// Send welcome email via EmailJS SDK
const sendWelcomeEmail = async (subscriberEmail) => {
  try {
    const SERVICE_ID = process.env.REACT_APP_EMAILJS_SERVICE_ID;
    const WELCOME_TEMPLATE_ID = process.env.REACT_APP_EMAILJS_WELCOME_TEMPLATE_ID;
    const PUBLIC_KEY = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;
    
    console.log('Sending welcome email to:', subscriberEmail);
    
    if (!SERVICE_ID || !WELCOME_TEMPLATE_ID || !PUBLIC_KEY) {
      console.error('EmailJS credentials missing from .env');
      return;
    }

    // Using EmailJS SDK - NO MORE FETCH!
    const result = await emailjs.send(
      SERVICE_ID,
      WELCOME_TEMPLATE_ID,
      {
        to_email: subscriberEmail  // Try user_email as primary
      },
      PUBLIC_KEY
    );

    console.log('✅ Welcome email sent successfully!', result);
  } catch (error) {
    console.error('❌ Welcome email error:', error.text || error);
  }
};

// Send contact notification email via EmailJS SDK
const sendEmailNotification = async (formData) => {
  try {
    const SERVICE_ID = process.env.REACT_APP_EMAILJS_SERVICE_ID;
    const CONTACT_TEMPLATE_ID = process.env.REACT_APP_EMAILJS_CONTACT_TEMPLATE_ID;
    const PUBLIC_KEY = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;
    const ADMIN_EMAIL = process.env.REACT_APP_ADMIN_EMAIL || 'your-admin-email@example.com';
    
    if (!SERVICE_ID || !CONTACT_TEMPLATE_ID || !PUBLIC_KEY) {
      console.error('EmailJS credentials missing from .env');
      return;
    }

    // Using EmailJS SDK - NO MORE FETCH!
    const result = await emailjs.send(
      SERVICE_ID,
      CONTACT_TEMPLATE_ID,
      {
        to_email: ADMIN_EMAIL,
        from_name: formData.name,
        from_email: formData.email,
        message: formData.message,
        reply_to: formData.email
      },
      PUBLIC_KEY
    );

    console.log('✅ Contact notification sent!', result);
  } catch (error) {
    console.error('❌ Contact notification error:', error.text || error);
  }
};

const Contact = () => {
  // Newsletter state
  const [email, setEmail] = useState("");
  const [subscribeMessage, setSubscribeMessage] = useState("");
  const [subscribing, setSubscribing] = useState(false);

  // Contact form state
  const [contactForm, setContactForm] = useState({
    name: "",
    email: "",
    message: ""
  });
  const [contactMessage, setContactMessage] = useState("");
  const [sending, setSending] = useState(false);

  const handleSubscribe = async (e) => {
    e.preventDefault();
    
    if (!email || !email.includes("@")) {
      setSubscribeMessage("Please enter a valid email address.");
      return;
    }

    setSubscribing(true);
    setSubscribeMessage("");

    try {
      const cleanEmail = email.toLowerCase().trim();

      // First, check if email already exists in database
      const existingSubscribers = await databases.listDocuments(
        DATABASE_ID,
        NEWSLETTER_COLLECTION_ID,
        [Query.equal('email', cleanEmail)]
      );

      // If email already exists, show message and stop
      if (existingSubscribers.documents.length > 0) {
        setSubscribeMessage("⚠️ This email is already subscribed!");
        setEmail("");
        setSubscribing(false);
        return; // Stop here, don't insert or send email
      }

      // Email doesn't exist, so create new subscription
      await databases.createDocument(
        DATABASE_ID,
        NEWSLETTER_COLLECTION_ID,
        ID.unique(),
        {
          email: cleanEmail,
          subscribedAt: new Date().toISOString(),
          isActive: true
        }
      );

      // Only send welcome email after successful subscription
      await sendWelcomeEmail(cleanEmail);
      
      setSubscribeMessage("🎉 Thank you for subscribing! Check your email for confirmation.");
      setEmail("");

    } catch (error) {
      console.error("Subscription error:", error);
      setSubscribeMessage("❌ Something went wrong. Please try again.");
    } finally {
      setSubscribing(false);
    }
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();

    if (!contactForm.name || !contactForm.email || !contactForm.message) {
      setContactMessage("Please fill in all fields.");
      return;
    }

    if (!contactForm.email.includes("@")) {
      setContactMessage("Please enter a valid email address.");
      return;
    }

    setSending(true);
    setContactMessage("");

    try {
      // Save to Appwrite database
      await databases.createDocument(
        DATABASE_ID,
        CONTACT_COLLECTION_ID,
        ID.unique(),
        {
          name: contactForm.name.trim(),
          email: contactForm.email.toLowerCase().trim(),
          message: contactForm.message.trim(),
          submittedAt: new Date().toISOString(),
          status: "unread"
        }
      );

      setContactMessage("✅ Message sent successfully! We'll get back to you soon.");
      
      // Send notification email to admin via EmailJS SDK
      await sendEmailNotification(contactForm);
      
      setContactForm({ name: "", email: "", message: "" });

    } catch (error) {
      console.error("Contact form error:", error);
      setContactMessage("Failed to send message. Please try again or email us directly.");
    } finally {
      setSending(false);
    }
  };

  const handleContactChange = (e) => {
    setContactForm({
      ...contactForm,
      [e.target.id]: e.target.value
    });
  };

  return (
    <>
      {/* Subscribe Section */}
      <section id="subscribe" className="py-16 px-6 bg-blue-100" style={{ backgroundColor: "#f5f5dc"}}>
        <h3 className="text-3xl font-semibold text-center mb-6">Subscribe to Our Newsletter</h3>
        <form
          onSubmit={handleSubscribe}
          className="max-w-md mx-auto bg-white p-6 shadow-md rounded-lg"
        >
          <p className="text-sm mb-4 text-center">
            Stay updated with the latest poems and news from PoemExtra. Enter your email to subscribe!
          </p>
          <div className="flex items-center mb-4">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="flex-grow border border-gray-300 p-2 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Your Email"
              disabled={subscribing}
              required
            />
            <button
              type="submit"
              disabled={subscribing}
              className="ml-2 bg-blue-800 text-white py-2 px-4 rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
            >
              {subscribing ? "..." : "Subscribe"}
            </button>
          </div>
          {subscribeMessage && (
            <p className={`text-center text-sm ${subscribeMessage.includes("🎉") || subscribeMessage.includes("✅") ? "text-green-600" : "text-red-600"}`}>
              {subscribeMessage}
            </p>
          )}
        </form>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-16 px-6">
        <h3 className="text-3xl font-semibold text-center mb-6">Contact Us</h3>
        <form onSubmit={handleContactSubmit} className="max-w-md mx-auto bg-white p-6 shadow-md rounded-lg">
          <div className="mb-4">
            <label htmlFor="name" className="block text-sm font-medium mb-2">
              Name
            </label>
            <input
              type="text"
              id="name"
              value={contactForm.name}
              onChange={handleContactChange}
              className="w-full border border-gray-300 p-2 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Your Name"
              disabled={sending}
              required
            />
          </div>
          <div className="mb-4">
            <label htmlFor="email" className="block text-sm font-medium mb-2">
              Email
            </label>
            <input
              type="email"
              id="email"
              value={contactForm.email}
              onChange={handleContactChange}
              className="w-full border border-gray-300 p-2 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Your Email"
              disabled={sending}
              required
            />
          </div>
          <div className="mb-4">
            <label htmlFor="message" className="block text-sm font-medium mb-2">
              Message
            </label>
            <textarea
              id="message"
              rows="4"
              value={contactForm.message}
              onChange={handleContactChange}
              className="w-full border border-gray-300 p-2 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Your Message"
              disabled={sending}
              required
            ></textarea>
          </div>
          <button
            type="submit"
            disabled={sending}
            className="bg-blue-800 text-white py-2 px-4 rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors w-full"
          >
            {sending ? "Sending..." : "Send Message"}
          </button>
          {contactMessage && (
            <p className={`text-center text-sm mt-4 ${contactMessage.includes("✅") ? "text-green-600" : "text-red-600"}`}>
              {contactMessage}
            </p>
          )}
        </form>
      </section>
    </>
  );
};

export default Contact;